import { ReactNode } from 'react';

interface ButtonProps {
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'outline' | 'cta' | 'navigation' | 'submit' | 'download';
  icon?: string;
  iconPosition?: 'left' | 'right';
  href?: string;
  className?: string;
  onClick?: () => void;
  type?: 'button' | 'submit' | 'reset';
  target?: string;
  rel?: string;
}

export default function Button({
  children,
  variant = 'primary',
  icon,
  iconPosition = 'left',
  href,
  className = '',
  onClick,
  type = 'button',
  target,
  rel,
}: ButtonProps) {
  const baseStyles = `
    relative font-bold rounded-lg transition-all duration-300 
    hover:scale-105 whitespace-nowrap cursor-pointer
    inline-flex items-center justify-center gap-2
  `;

  const variantStyles: Record<NonNullable<ButtonProps['variant']>, string> = {
    primary: `
      px-6 py-3 bg-gradient-to-r from-red-600 to-red-700
      text-white border-2 border-red-500/50
      hover:from-red-700 hover:to-red-800
    `,
    secondary: `
      px-6 py-3 bg-gray-800 hover:bg-gray-700 text-white 
      border-2 border-gray-600 hover:border-red-500
    `,
    outline: `
      px-6 py-3 bg-transparent text-white 
      border-2 border-red-600/50 hover:border-red-600
      hover:bg-red-600/10
    `,
    cta: `
      px-8 py-4 bg-gradient-to-r from-red-600 to-red-700
      text-white border-2 border-red-500/50
      hover:from-red-700 hover:to-red-800
      uppercase tracking-wider
    `,
    navigation: `
      px-5 py-2.5 bg-white/10 hover:bg-white/20 text-white text-sm
      backdrop-blur-sm border border-white/20
    `,
    submit: `
      px-8 py-4 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800
      text-white border-2 border-red-500/50
      uppercase tracking-wider
    `,
    download: `
      px-6 py-3 bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800
      text-white border-2 border-red-500/50
    `,
  };

  const combinedClassName = `${baseStyles} ${variantStyles[variant]} ${className}`.trim();

  const content = (
    <>
      {icon && iconPosition === 'left' && <i className={`${icon} text-lg`} />}
      <span>{children}</span>
      {icon && iconPosition === 'right' && <i className={`${icon} text-lg`} />}
    </>
  );

  if (href) {
    const safeRel =
      target === '_blank'
        ? rel
          ? `${rel} noopener noreferrer`
          : 'noopener noreferrer'
        : rel ?? undefined;

    return (
      <a href={href} className={combinedClassName} onClick={onClick} target={target} rel={safeRel}>
        {content}
      </a>
    );
  }

  return (
    <button type={type} className={combinedClassName} onClick={onClick}>
      {content}
    </button>
  );
}
